/**
 * Settings → Lead follow-up.
 *
 * Two things the dealer asked to control in the September showcase:
 *   1. When a lead counts as unactioned and who gets told — "this lead has not
 *      been actioned for 24 hours, or no task is assigned for this."
 *   2. The reason list behind every next step — "make appointment",
 *      "follow up", "confirm delivery" — which is what a consultant picks from.
 *
 * Both were SQL-only until this panel existed.
 */

import { useEffect, useMemo, useState } from 'react';
import { leadFollowUpService } from '../../lib/services';
import { useCurrentUser } from '../../lib/currentUser';
import { useToast } from '../../lib/toast';
import { APPT_TYPES } from '../../lib/appointmentMeta';

const ROLE_OPTIONS = [['SALES_MANAGER', 'Sales Manager'], ['DEALER_PRINCIPAL', 'Dealer Principal']];
const APPLIES = [['LEAD', 'Leads'], ['CUSTOMER', 'Customers'], ['BOTH', 'Both']];
const TASK_TYPES = ['CALL', 'EMAIL', 'FOLLOW_UP', 'MEETING', 'ADMIN'];

const asList = (v) => (Array.isArray(v) ? v : (v?.data ?? []));

/**
 * Writes to /v1/settings/* are DEALER_PRINCIPAL / SUPER_ADMIN only on the
 * server. Mirror that here so a sales manager sees the rules read-only rather
 * than a Save button that 403s — the server stays the authority, this is just
 * not lying to the user about what they can do.
 */
const useCanEdit = () => {
  // Shared capability, not a private role list — see LocationsPanel for the
  // GM/CEO lockout the old copy caused.
  const { can } = useCurrentUser();
  return can('settings.edit');
};

function ReadOnlyNote() {
  return (
    <div className="mono text-xs text-muted" style={{ marginBottom: 8 }}>
      Read-only — only a Dealer Principal can change these.
    </div>
  );
}

export default function LeadFollowUpPanel() {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      <SlaSection />
      <ReasonsSection />
    </div>
  );
}

/* ─────────────────────────────── SLA ─────────────────────────────── */

function SlaSection() {
  const canEdit = useCanEdit();
  const { toast } = useToast();
  const [form, setForm] = useState(null);
  const [saved, setSaved] = useState(null);
  const [busy, setBusy] = useState(false);
  // Operator switch, reported by the API — not something this panel can set.
  const [senderEnabled, setSenderEnabled] = useState(true);
  const [error, setError] = useState(null);

  const load = async () => {
    try {
      const cfg = await leadFollowUpService.getSla();
      const f = {
        firstResponseMinutes: cfg?.firstResponseMinutes ?? 30,
        unactionedHours: cfg?.unactionedHours ?? 24,
        requireNextEvent: cfg?.requireNextEvent ?? true,
        escalateToRole: cfg?.escalateToRole ?? 'SALES_MANAGER',
        businessHoursOnly: cfg?.businessHoursOnly ?? true,
        nurtureExpiryWarnDays: cfg?.nurtureExpiryWarnDays ?? 7,
        // These four were missing, so the `?? default` fallbacks further down
        // were always the ones rendering: a dealership that had saved a 30-day
        // window was shown 60 on every reload, and a save from that screen
        // silently wrote 60 back. The settings were real; the form was lying
        // about them.
        nurtureWindowDays: cfg?.nurtureWindowDays ?? 60,
        nurtureFrequencyDays: cfg?.nurtureFrequencyDays ?? 7,
        nurtureMaxAttempts: cfg?.nurtureMaxAttempts ?? 8,
        nurtureAutoLost: cfg?.nurtureAutoLost !== false,
        nurtureChannel: cfg?.nurtureChannel ?? 'WHATSAPP',
        nurtureDailyCap: cfg?.nurtureDailyCap ?? 50,
      };
      setSenderEnabled(cfg?.nurtureSenderEnabled === true);
      setForm(f); setSaved(f); setError(null);
    } catch (e) {
      setError(e);
    }
  };
  useEffect(() => { load(); }, []);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const dirty = form && saved && JSON.stringify(form) !== JSON.stringify(saved);

  const save = async () => {
    setBusy(true);
    try {
      await leadFollowUpService.putSla(form);
      setSaved(form);
      toast('Escalation rules saved', { variant: 'success' });
    } catch (e) {
      toast(`Not saved: ${e?.response?.data?.message || e?.message || 'unknown error'}`, { variant: 'danger' });
    } finally {
      setBusy(false);
    }
  };

  if (error) {
    return (
      <div className="card">
        <div className="card-label">ESCALATION RULES</div>
        <div className="mono text-xs" style={{ color: 'var(--danger)', marginTop: 6 }}>
          Couldn't load — the settings endpoint may not be deployed yet.{' '}
          <button type="button" className="btn small" onClick={load}>Retry</button>
        </div>
      </div>
    );
  }
  if (!form) return <div className="card"><div className="card-label">ESCALATION RULES</div><div className="mono text-xs text-muted">loading…</div></div>;

  return (
    <div className="card">
      <div className="flex-between" style={{ marginBottom: 4 }}>
        <span className="card-label" style={{ margin: 0 }}>ESCALATION RULES</span>
        <div className="flex-row" style={{ gap: 6 }}>
          {canEdit && dirty && <button type="button" className="btn small" onClick={() => setForm(saved)} disabled={busy}>Discard</button>}
          {canEdit && <button type="button" className="btn small primary" onClick={save} disabled={!dirty || busy}>{busy ? 'Saving…' : 'Save'}</button>}
        </div>
      </div>
      <div className="mono text-xs text-muted" style={{ marginBottom: 12 }}>
        When a lead is flagged to the {form.escalateToRole === 'DEALER_PRINCIPAL' ? 'Dealer Principal' : 'Sales Manager'}. Checked every 15 minutes.
      </div>

      {!canEdit && <ReadOnlyNote />}

      <fieldset disabled={!canEdit} style={{ border: 0, padding: 0, margin: 0, minWidth: 0 }}>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
        <div className="form-field">
          <label>New lead must be actioned within</label>
          <div className="flex-row" style={{ alignItems: 'center', gap: 6 }}>
            <input className="input input-compact" type="number" min={1} max={1440} value={form.firstResponseMinutes} onChange={(e) => set('firstResponseMinutes', Number(e.target.value))} style={{ width: 90 }} />
            <span className="text-muted">minutes</span>
          </div>
        </div>
        <div className="form-field">
          <label>Open lead counts as unactioned after</label>
          <div className="flex-row" style={{ alignItems: 'center', gap: 6 }}>
            <input className="input input-compact" type="number" min={1} max={720} value={form.unactionedHours} onChange={(e) => set('unactionedHours', Number(e.target.value))} style={{ width: 90 }} />
            <span className="text-muted">hours</span>
          </div>
        </div>
        <div className="form-field">
          <label>Escalate to</label>
          <select className="input input-compact" value={form.escalateToRole} onChange={(e) => set('escalateToRole', e.target.value)}>
            {ROLE_OPTIONS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
          </select>
        </div>
        <div className="form-field">
          <label>Warn when AI nurture expires within</label>
          <div className="flex-row" style={{ alignItems: 'center', gap: 6 }}>
            <input className="input input-compact" type="number" min={0} max={60} value={form.nurtureExpiryWarnDays} onChange={(e) => set('nurtureExpiryWarnDays', Number(e.target.value))} style={{ width: 90 }} />
            <span className="text-muted">days</span>
          </div>
        </div>
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginTop: 10 }}>
        <label className="flex-row" style={{ alignItems: 'center', gap: 8, cursor: 'pointer' }}>
          <input type="checkbox" checked={!!form.requireNextEvent} onChange={(e) => set('requireNextEvent', e.target.checked)} />
          <span>Flag any open lead that has no next step</span>
        </label>
        <label className="flex-row" style={{ alignItems: 'center', gap: 8, cursor: 'pointer' }}>
          <input type="checkbox" checked={!!form.businessHoursOnly} onChange={(e) => set('businessHoursOnly', e.target.checked)} />
          <span>Only raise new flags during business hours <span className="text-muted">(a Saturday-evening lead isn't "24h unactioned" by Sunday)</span></span>
        </label>
      </div>

      {/* E5 / T046-T047. The window, the cadence and the cap fail in different
          ways — a long window wastes weeks, a tight cadence annoys someone
          inside one week — so a dealer can be wrong about each independently
          and has to be able to fix each independently. */}
      <div className="card-label" style={{ margin: '18px 0 4px' }}>AI NURTURE</div>
      <div className="mono text-xs text-muted" style={{ marginBottom: 12 }}>
        How long the AI keeps trying a lead nobody has replied to, and what happens when it runs out.
      </div>

      {/* A settings screen that accepts a cadence the system will never act on
          is worse than one that omits the field — the dealer walks away
          believing follow-ups are going out. Say so where they are typing. */}
      {!senderEnabled && (
        <div
          className="mono text-xs"
          role="status"
          style={{
            marginBottom: 12, padding: '8px 10px', borderRadius: 6,
            background: 'color-mix(in srgb, var(--warning) 12%, transparent)',
            color: 'var(--warning)',
          }}
        >
          Automatic follow-ups are switched off in this environment, so the cadence and
          attempt limit below will not send anything yet. The window still applies —
          leads still expire and are marked Lost on schedule.
        </div>
      )}

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 12 }}>
        <div className="form-field">
          <label>Stop nurturing after</label>
          <div className="flex-row" style={{ alignItems: 'center', gap: 6 }}>
            <input className="input input-compact" type="number" min={1} max={365} value={form.nurtureWindowDays ?? 60}
                   onChange={(e) => set('nurtureWindowDays', Number(e.target.value))} style={{ width: 90 }} />
            <span className="text-muted">days</span>
          </div>
        </div>
        <div className="form-field">
          <label>At most one attempt every</label>
          <div className="flex-row" style={{ alignItems: 'center', gap: 6 }}>
            <input className="input input-compact" type="number" min={1} max={90} value={form.nurtureFrequencyDays ?? 7}
                   onChange={(e) => set('nurtureFrequencyDays', Number(e.target.value))} style={{ width: 90 }} />
            <span className="text-muted">days</span>
          </div>
        </div>
        <div className="form-field">
          <label>Never more than</label>
          <div className="flex-row" style={{ alignItems: 'center', gap: 6 }}>
            <input className="input input-compact" type="number" min={1} max={50} value={form.nurtureMaxAttempts ?? 8}
                   onChange={(e) => set('nurtureMaxAttempts', Number(e.target.value))} style={{ width: 90 }} />
            <span className="text-muted">attempts</span>
          </div>
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 12, marginTop: 12 }}>
        <div className="form-field">
          <label>Send follow-ups by</label>
          <select className="input input-compact" value={form.nurtureChannel ?? 'WHATSAPP'}
                  onChange={(e) => set('nurtureChannel', e.target.value)}>
            <option value="WHATSAPP">WhatsApp</option>
            <option value="SMS">SMS</option>
          </select>
        </div>
        {/* Not a dealer-facing tuning knob so much as a blast radius: if the
            cadence is ever misconfigured, this is what turns it into a trickle
            instead of a bulk send. */}
        <div className="form-field">
          <label>No more than</label>
          <div className="flex-row" style={{ alignItems: 'center', gap: 6 }}>
            <input className="input input-compact" type="number" min={1} max={1000} value={form.nurtureDailyCap ?? 50}
                   onChange={(e) => set('nurtureDailyCap', Number(e.target.value))} style={{ width: 90 }} />
            <span className="text-muted">sends an hour</span>
          </div>
        </div>
      </div>

      <label className="flex-row" style={{ alignItems: 'center', gap: 8, cursor: 'pointer', marginTop: 10 }}>
        <input type="checkbox" checked={form.nurtureAutoLost !== false}
               onChange={(e) => set('nurtureAutoLost', e.target.checked)} />
        <span>
          Mark the lead Lost when the window closes{' '}
          <span className="text-muted">
            (reason recorded as “nurture expired”; it stays in the revival pool. Turn this off and the
            deadline still raises a flag, but a person decides.)
          </span>
        </span>
      </label>
      </fieldset>
    </div>
  );
}

/* ───────────────────────────── Reasons ───────────────────────────── */

function ReasonsSection() {
  const canEdit = useCanEdit();
  const { toast } = useToast();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showInactive, setShowInactive] = useState(false);
  const [adding, setAdding] = useState(false);
  const [draft, setDraft] = useState({ code: '', label: '', appliesTo: 'LEAD', defaultType: 'CALL', convertsToAppointment: false, appointmentType: '' });
  const [busyCode, setBusyCode] = useState(null);

  const load = async () => {
    setLoading(true);
    try { setRows(asList(await leadFollowUpService.listReasons())); setError(null); }
    catch (e) { setError(e); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const visible = useMemo(() => rows.filter((r) => showInactive || r.active !== false), [rows, showInactive]);

  const patch = async (code, body, ok) => {
    setBusyCode(code);
    try {
      const next = await leadFollowUpService.updateReason(code, body);
      setRows(asList(next));
      if (ok) toast(ok, { variant: 'success' });
    } catch (e) {
      toast(`Not saved: ${e?.response?.data?.message || e?.message || 'unknown error'}`, { variant: 'danger' });
    } finally {
      setBusyCode(null);
    }
  };

  const create = async () => {
    if (!draft.code.trim() || !draft.label.trim()) return;
    setBusyCode('__new');
    try {
      const next = await leadFollowUpService.createReason({
        ...draft,
        appointmentType: draft.convertsToAppointment ? (draft.appointmentType || 'TEST_DRIVE') : null,
        sortOrder: (rows.reduce((m, r) => Math.max(m, r.sortOrder ?? 0), 0) || 0) + 10,
      });
      setRows(asList(next));
      setAdding(false);
      setDraft({ code: '', label: '', appliesTo: 'LEAD', defaultType: 'CALL', convertsToAppointment: false, appointmentType: '' });
      toast('Reason added', { variant: 'success' });
    } catch (e) {
      toast(`Not added: ${e?.response?.data?.message || e?.message || 'unknown error'}`, { variant: 'danger' });
    } finally {
      setBusyCode(null);
    }
  };

  const move = (code, dir) => {
    const idx = visible.findIndex((r) => r.code === code);
    const swap = visible[idx + dir];
    if (!swap) return;
    const a = visible[idx];
    // Swap sort orders; if equal, nudge so the move is visible.
    const ao = a.sortOrder ?? 0, bo = swap.sortOrder ?? 0;
    const na = ao === bo ? bo + dir * 5 : bo;
    const nb = ao === bo ? ao : ao;
    Promise.all([
      leadFollowUpService.updateReason(a.code, { sortOrder: na }),
      leadFollowUpService.updateReason(swap.code, { sortOrder: nb }),
    ]).then((res) => setRows(asList(res[res.length - 1]))).catch(() => toast('Could not reorder', { variant: 'danger' }));
  };

  return (
    <div className="card">
      <div className="flex-between" style={{ marginBottom: 4 }}>
        <span className="card-label" style={{ margin: 0 }}>NEXT-STEP REASONS</span>
        <div className="flex-row" style={{ gap: 12, alignItems: 'center' }}>
          <label className="mono text-xs text-muted" style={{ display: 'inline-flex', alignItems: 'center', gap: 6, cursor: 'pointer' }}>
            <input type="checkbox" checked={showInactive} onChange={(e) => setShowInactive(e.target.checked)} /> show retired
          </label>
          {canEdit && <button type="button" className="btn small primary" onClick={() => setAdding((v) => !v)}>{adding ? 'Cancel' : '+ Add reason'}</button>}
        </div>
      </div>
      <div className="mono text-xs text-muted" style={{ marginBottom: 12 }}>
        What a consultant picks when setting the next step on a lead or customer. Reasons that convert to an appointment offer the booking the moment the task is completed.
      </div>

      {!canEdit && <ReadOnlyNote />}

      {error && (
        <div className="mono text-xs" style={{ color: 'var(--danger)', marginBottom: 8 }}>
          Couldn't load — the settings endpoint may not be deployed yet. <button type="button" className="btn small" onClick={load}>Retry</button>
        </div>
      )}

      {adding && (
        <div className="card" style={{ padding: 12, marginBottom: 12, background: 'var(--bg-glass)' }}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 2fr', gap: 10 }}>
            <div className="form-field">
              <label>Code</label>
              <input value={draft.code} onChange={(e) => setDraft((d) => ({ ...d, code: e.target.value.toUpperCase().replace(/[^A-Z0-9_]/g, '_') }))} placeholder="CHASE_TRADE_DOCS" />
            </div>
            <div className="form-field">
              <label>Label</label>
              <input value={draft.label} onChange={(e) => setDraft((d) => ({ ...d, label: e.target.value }))} placeholder="Chase trade-in documents" autoFocus />
            </div>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 10 }}>
            <div className="form-field">
              <label>Applies to</label>
              <select value={draft.appliesTo} onChange={(e) => setDraft((d) => ({ ...d, appliesTo: e.target.value }))}>
                {APPLIES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
              </select>
            </div>
            <div className="form-field">
              <label>Default task type</label>
              <select value={draft.defaultType} onChange={(e) => setDraft((d) => ({ ...d, defaultType: e.target.value }))}>
                {TASK_TYPES.map((t) => <option key={t} value={t}>{t.replace('_', ' ')}</option>)}
              </select>
            </div>
            <div className="form-field">
              <label>On completion, book</label>
              <select
                value={draft.convertsToAppointment ? (draft.appointmentType || 'TEST_DRIVE') : ''}
                onChange={(e) => setDraft((d) => ({ ...d, convertsToAppointment: !!e.target.value, appointmentType: e.target.value }))}
              >
                <option value="">— nothing —</option>
                {APPT_TYPES.filter((t) => t.stream === 'SALES').map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
              </select>
            </div>
          </div>
          <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
            <button type="button" className="btn small primary" onClick={create} disabled={busyCode === '__new' || !draft.code.trim() || !draft.label.trim()}>
              {busyCode === '__new' ? 'Adding…' : 'Add'}
            </button>
          </div>
        </div>
      )}

      {loading && !rows.length ? (
        <div className="mono text-xs text-muted">loading…</div>
      ) : (
        <div className="table-scroll">
          <table className="data-table">
            <thead>
              <tr><th style={{ width: 60 }} /><th>Label</th><th>Code</th><th>Applies to</th><th>Type</th><th>Books</th><th style={{ textAlign: 'right' }} /></tr>
            </thead>
            <tbody>
              {visible.map((r, i) => {
                const busy = busyCode === r.code;
                const retired = r.active === false;
                return (
                  <tr key={r.code} style={{ opacity: retired ? 0.55 : 1 }}>
                    <td>
                      <button type="button" className="btn small" disabled={!canEdit || i === 0 || busy || retired} onClick={() => move(r.code, -1)} aria-label="Move up">▲</button>
                      <button type="button" className="btn small" disabled={!canEdit || i === visible.length - 1 || busy || retired} onClick={() => move(r.code, 1)} aria-label="Move down">▼</button>
                    </td>
                    <td>
                      <input
                        className="input input-compact"
                        defaultValue={r.label}
                        disabled={!canEdit || retired || busy}
                        onBlur={(e) => { const v = e.target.value.trim(); if (v && v !== r.label) patch(r.code, { label: v }, 'Renamed'); }}
                        style={{ width: '100%' }}
                      />
                    </td>
                    <td className="mono text-xs text-muted">{r.code}</td>
                    <td>
                      <select className="input input-compact" value={r.appliesTo} disabled={!canEdit || retired || busy} onChange={(e) => patch(r.code, { appliesTo: e.target.value })}>
                        {APPLIES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                      </select>
                    </td>
                    <td>
                      <select className="input input-compact" value={r.defaultType || 'CALL'} disabled={!canEdit || retired || busy} onChange={(e) => patch(r.code, { defaultType: e.target.value })}>
                        {TASK_TYPES.map((t) => <option key={t} value={t}>{t.replace('_', ' ')}</option>)}
                      </select>
                    </td>
                    <td>
                      <select
                        className="input input-compact"
                        value={r.convertsToAppointment ? (r.appointmentType || 'TEST_DRIVE') : ''}
                        disabled={!canEdit || retired || busy}
                        onChange={(e) => patch(r.code, { convertsToAppointment: !!e.target.value, appointmentType: e.target.value || '' })}
                      >
                        <option value="">—</option>
                        {APPT_TYPES.filter((t) => t.stream === 'SALES').map((t) => <option key={t.value} value={t.value}>{t.short}</option>)}
                      </select>
                    </td>
                    <td style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>
                      {retired ? (
                        <button type="button" className="btn small" disabled={!canEdit || busy} onClick={() => patch(r.code, { active: true }, 'Restored')}>Restore</button>
                      ) : (
                        <button type="button" className="btn small danger" disabled={!canEdit || busy} onClick={() => patch(r.code, { active: false }, 'Retired — existing tasks keep it')}>Retire</button>
                      )}
                    </td>
                  </tr>
                );
              })}
              {!visible.length && !loading && (
                <tr><td colSpan={7} className="mono text-xs text-muted" style={{ padding: 16 }}>No reasons yet.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
